import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { HeartHandshake, LockOpen } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, BulkAction, Selection } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchBanned, unbanUser, type AdminBannedUser } from '@/lib/adapters'
import { BAN_STR as S } from './strikesApi'

/**
 * A8.13 — Ommaviy amnistiya. JONLI `/admin/banned`; belgilangan userlarga
 * ommaviy unban (`/admin/users/:id/unban` bo'yicha aylanadi). «Filtr bo'yicha
 * hammasi» rejimi qo'llab-quvvatlanmaydi (aniq tanlov talab qilinadi).
 */
export function MassAmnestyScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const runBulk = useCallback(
    async (sel: Selection<AdminBannedUser>) => {
      if (sel.mode !== 'rows' || sel.rows.length === 0) {
        toast(S.selectRowsFirst, { tone: 'warning' })
        return
      }
      const rows = sel.rows
      if (!window.confirm(`${S.bulkUnban} (${rows.length})?`)) return
      const results = await Promise.allSettled(rows.map((r) => unbanUser(r.id)))
      const ok = results.filter((x) => x.status === 'fulfilled').length
      const failed = results.length - ok
      toast(failed === 0 ? `${S.amnestyDone} (${ok})` : `${S.amnestyPartial}: ${ok}/${results.length}`, {
        tone: failed === 0 ? 'success' : 'warning',
      })
      setReloadKey((k) => k + 1)
    },
    [toast],
  )

  const bulkActions = useMemo<BulkAction<AdminBannedUser>[]>(
    () => [
      {
        id: 'bulk-unban',
        label: S.bulkUnban,
        icon: <LockOpen size={13} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (sel) => void runBulk(sel),
      },
    ],
    [runBulk],
  )

  const columns = useMemo<AdminColumn<AdminBannedUser>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 80 },
      {
        key: 'username',
        header: S.userId,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.id}`}
          </Link>
        ),
      },
      { key: 'banned_reason', header: S.reason, type: 'longtext', minWidth: 220 },
      { key: 'banned_at', header: S.bannedAt, type: 'date', width: 160 },
    ],
    [],
  )

  const fetcher = useCallback(fetchBanned, [])
  return (
    <>
      <PageHeader title={S.amnesty} screenId="A8.13" description={S.amnestyNote} />
      <DataTable<AdminBannedUser>
        key={reloadKey}
        screenId="A8.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        selectable
        bulkActions={bulkActions}
        exportable
        emptyState={{ icon: <HeartHandshake size={28} strokeWidth={1.5} />, title: S.amnesty }}
      />
    </>
  )
}
